import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  Label,
  DocumentTextIcon,
  UserGroupIcon,
  BuildingIcon,
  ClipboardIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '../../../components/ui';
import { TICKETING_FORM_KEY } from '../ticketingFormKeys';

const HR_RESOURCE_LABELS = {
  email: 'Email',
  windows_account: 'Windows account',
  erp: 'ERP',
  wms: 'WMS',
  pc_laptop: 'PC / Laptop',
  mobile: 'Mobile',
  ip_phone: 'IP Phone',
  access_card: 'Access card',
  rf_scanner: 'RF (scanner)',
};

const WAREHOUSE_KIND_LABELS = {
  stock: 'Stock / inventory',
  equipment: 'Equipment',
  access: 'Access / badges',
};

const URGENCY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical',
  urgent: 'Urgent',
};

const IMPACT_LABELS = {
  all_company: 'All company',
  my_department: 'My department',
  only_me: 'Only me',
};

const IT_CATEGORY_LABELS = {
  hardware: 'Hardware',
  software: 'Software',
  other: 'Other',
};

function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayValue(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.map(String).join(', ') : null;
  if (typeof v === 'object') return null;
  return String(v);
}

function formatPayloadDate(value) {
  if (value == null || String(value).trim() === '') return null;
  const raw = String(value).trim();
  const d = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T12:00:00`)
    : new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function EmptyHint({ children }) {
  const { theme } = useTheme();
  return (
    <p className={cn('text-xs italic m-0', theme.text.muted)}>{children}</p>
  );
}

/**
 * Single payload block: left-aligned header + content, minimal chrome.
 */
function PayloadBlock({ icon: Icon, title, children, className }) {
  const { theme } = useTheme();
  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden',
        'bg-white/70 dark:bg-slate-900/40',
        theme.border.light,
        className
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 border-b',
          'border-slate-200/90 dark:border-slate-700/80',
          'bg-slate-50/80 dark:bg-slate-800/40'
        )}
      >
        {Icon ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-100/90 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
        <span className={cn('text-sm font-medium', theme.text.primary)}>{title}</span>
      </div>
      <div className={cn('px-3 py-2.5', theme.colors.surface)}>{children}</div>
    </div>
  );
}

function InlineField({ label, value, className }) {
  const { theme } = useTheme();
  const shown = displayValue(value);
  if (shown == null) return null;
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <span className={cn('text-xs text-slate-500 dark:text-slate-400')}>{label}</span>
      <span className={cn('text-sm', theme.text.primary)}>{shown}</span>
    </div>
  );
}

function ProseBlock({ children, className }) {
  const { theme } = useTheme();
  if (children == null || String(children).trim() === '') return null;
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm leading-relaxed',
        'border-slate-200/90 dark:border-slate-700/80',
        'bg-slate-50/50 dark:bg-slate-950/30',
        theme.text.primary,
        className
      )}
    >
      <p className="whitespace-pre-wrap break-words m-0">{children}</p>
    </div>
  );
}

function SelectedResources({ itResources }) {
  if (!itResources || typeof itResources !== 'object') {
    return <EmptyHint>No resources selected.</EmptyHint>;
  }
  const selected = Object.entries(itResources).filter(([, on]) => Boolean(on));
  if (selected.length === 0) {
    return <EmptyHint>No resources selected.</EmptyHint>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {selected.map(([id]) => (
        <Badge
          key={id}
          variant="success"
          size="sm"
          className="font-normal text-xs"
          icon={<CheckCircleIcon className="h-3 w-3 opacity-90" />}
        >
          {HR_RESOURCE_LABELS[id] || humanizeKey(id)}
        </Badge>
      ))}
    </div>
  );
}

function SiteCodePill({ code }) {
  if (code == null || code === '') return null;
  const codes = Array.isArray(code)
    ? code.filter((v) => v != null && String(v).trim() !== '').map((v) => String(v))
    : [String(code)];
  if (codes.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {codes.map((siteCode) => (
        <span
          key={siteCode}
          className="inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-md bg-blue-600 px-2 text-sm font-bold text-white shadow-sm"
        >
          {siteCode}
        </span>
      ))}
      <span className="text-xs text-slate-500 dark:text-slate-400">Selected site(s)</span>
    </div>
  );
}

function MetaChips({ items }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {items.map(({ label, value, variant = 'secondary' }) =>
        value != null && value !== '' ? (
          <div key={label} className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
            <Badge variant={variant} size="sm" className="text-xs">
              {value}
            </Badge>
          </div>
        ) : null
      )}
    </div>
  );
}

function GenericPayloadTree({ data, depth = 0 }) {
  const { theme } = useTheme();
  if (data === null || data === undefined) {
    return <span className={cn('text-xs', theme.text.muted)}>—</span>;
  }
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <EmptyHint>Empty list</EmptyHint>;
    }
    return (
      <ul
        className={cn(
          'list-none m-0 p-0 space-y-1.5',
          depth > 0 && 'pl-3 border-l border-slate-200 dark:border-slate-700'
        )}
      >
        {data.map((item, i) => (
          <li key={i}>
            {typeof item === 'object' && item !== null ? (
              <GenericPayloadTree data={item} depth={depth + 1} />
            ) : (
              <div
                className={cn(
                  'rounded-md border px-2.5 py-1.5 text-sm',
                  theme.border.light,
                  theme.text.primary
                )}
              >
                {displayValue(item) ?? '—'}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <EmptyHint>No fields</EmptyHint>;
    }
    return (
      <div
        className={cn(
          'grid gap-2',
          depth > 0 && 'pl-3 border-l border-violet-200/70 dark:border-violet-900/50'
        )}
      >
        {entries.map(([k, v]) => (
          <div
            key={k}
            className={cn(
              'rounded-md border px-2.5 py-2',
              theme.border.light,
              theme.colors.surface
            )}
          >
            <Label className="mb-0.5 block text-xs font-medium text-slate-500 dark:text-slate-400 cursor-default">
              {humanizeKey(k)}
            </Label>
            {v !== null && typeof v === 'object' ? (
              <GenericPayloadTree data={v} depth={depth + 1} />
            ) : displayValue(v) != null ? (
              <span className={cn('text-sm break-words', theme.text.primary)}>{displayValue(v)}</span>
            ) : (
              <span className={cn('text-xs', theme.text.muted)}>—</span>
            )}
          </div>
        ))}
      </div>
    );
  }
  return <span className={cn('text-sm', theme.text.primary)}>{String(data)}</span>;
}

function hrEmployeeDateField(formKey, p) {
  if (formKey === TICKETING_FORM_KEY.HR_EMPLOYEE_EDIT_IT) {
    return { label: 'Change Date', value: p.changeDate };
  }
  if (formKey === TICKETING_FORM_KEY.HR_EMPLOYEE_DELETE_IT) {
    return { label: 'Withdrawal Date', value: p.withdrawalDate };
  }
  return { label: 'Recruitment Date', value: p.recruitmentDate };
}

function renderHrEmployeeItPayload(formKey, p) {
  const { label: dateLabel, value: dateRaw } = hrEmployeeDateField(formKey, p);
  return (
    <div className="space-y-2.5">
      <PayloadBlock icon={BuildingIcon} title="Employee details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
          <InlineField
            label="Department"
            value={p.userDepartmentName || (p.userDepartmentId != null ? `#${p.userDepartmentId}` : null)}
          />
          <InlineField label="Full name" value={p.employeeFullName} />
          <InlineField
            label={dateLabel}
            value={formatPayloadDate(dateRaw) ?? dateRaw}
          />
          <InlineField label="Working hours" value={p.workingHours} />
          <InlineField label="Location access" value={p.locationAccess} />
        </div>
      </PayloadBlock>
      <PayloadBlock icon={UserGroupIcon} title="Employee IT resources">
        <SelectedResources itResources={p.itResources} />
      </PayloadBlock>
      <PayloadBlock icon={BuildingIcon} title="Site / warehouse">
        {p.warehouse ? (
          <SiteCodePill code={p.warehouse} />
        ) : (
          <EmptyHint>Not specified.</EmptyHint>
        )}
      </PayloadBlock>
      <PayloadBlock icon={DocumentTextIcon} title="Comments">
        {p.comments ? (
          <ProseBlock>{p.comments}</ProseBlock>
        ) : (
          <EmptyHint>No comments.</EmptyHint>
        )}
      </PayloadBlock>
    </div>
  );
}

function renderByFormKey(formKey, payload) {
  const p = payload && typeof payload === 'object' ? payload : {};

  switch (formKey) {
    case TICKETING_FORM_KEY.HR_EMPLOYEE_IT:
    case TICKETING_FORM_KEY.HR_EMPLOYEE_EDIT_IT:
    case TICKETING_FORM_KEY.HR_EMPLOYEE_DELETE_IT:
      return renderHrEmployeeItPayload(formKey, p);

    case TICKETING_FORM_KEY.WAREHOUSE_ACCESS:
      return (
        <div className="space-y-2.5">
          <PayloadBlock icon={BuildingIcon} title="Location & type">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InlineField label="Location / zone" value={p.locationZone} />
              <InlineField
                label="Request type"
                value={
                  p.requestKind != null ? WAREHOUSE_KIND_LABELS[p.requestKind] || humanizeKey(p.requestKind) : null
                }
              />
            </div>
          </PayloadBlock>
          <PayloadBlock icon={ExclamationTriangleIcon} title="Priority">
            <MetaChips
              items={[
                {
                  label: 'Urgency',
                  value: p.urgency != null ? URGENCY_LABELS[p.urgency] || humanizeKey(p.urgency) : null,
                  variant: p.urgency === 'urgent' ? 'warning' : 'secondary',
                },
              ]}
            />
          </PayloadBlock>
          <PayloadBlock icon={ClipboardIcon} title="Comments">
            {p.comments ? (
              <ProseBlock>{p.comments}</ProseBlock>
            ) : (
              <EmptyHint>No comments.</EmptyHint>
            )}
          </PayloadBlock>
        </div>
      );

    case TICKETING_FORM_KEY.GENERAL:
      return (
        <div className="space-y-2.5">
          <PayloadBlock icon={DocumentTextIcon} title="Description">
            {p.description ? (
              <ProseBlock>{p.description}</ProseBlock>
            ) : (
              <EmptyHint>No description.</EmptyHint>
            )}
          </PayloadBlock>
          <PayloadBlock icon={TagIcon} title="Urgency & impact">
            <MetaChips
              items={[
                {
                  label: 'Urgency',
                  value: p.urgency != null ? URGENCY_LABELS[p.urgency] || humanizeKey(p.urgency) : null,
                  variant: p.urgency === 'critical' || p.urgency === 'high' ? 'danger' : 'secondary',
                },
                {
                  label: 'Impact',
                  value: p.impact != null ? IMPACT_LABELS[p.impact] || humanizeKey(p.impact) : null,
                  variant: 'outline',
                },
              ]}
            />
          </PayloadBlock>
        </div>
      );

    case TICKETING_FORM_KEY.GENERAL_IT:
      return (
        <div className="space-y-2.5">
          <PayloadBlock icon={DocumentTextIcon} title="Description">
            {p.description ? (
              <ProseBlock>{p.description}</ProseBlock>
            ) : (
              <EmptyHint>No description.</EmptyHint>
            )}
          </PayloadBlock>
          <PayloadBlock icon={TagIcon} title="Category & priority">
            <MetaChips
              items={[
                {
                  label: 'Category',
                  value:
                    p.itCategory != null ? IT_CATEGORY_LABELS[p.itCategory] || humanizeKey(p.itCategory) : null,
                  variant: 'default',
                },
                {
                  label: 'Urgency',
                  value: p.urgency != null ? URGENCY_LABELS[p.urgency] || humanizeKey(p.urgency) : null,
                  variant: p.urgency === 'critical' || p.urgency === 'high' ? 'danger' : 'secondary',
                },
                {
                  label: 'Impact',
                  value: p.impact != null ? IMPACT_LABELS[p.impact] || humanizeKey(p.impact) : null,
                  variant: 'outline',
                },
              ]}
            />
          </PayloadBlock>
        </div>
      );

    default:
      return (
        <PayloadBlock icon={DocumentTextIcon} title="Request details">
          <GenericPayloadTree data={p} />
        </PayloadBlock>
      );
  }
}

/**
 * Form-aware display of ticketing request payload (no raw JSON).
 */
export function TicketingPayloadViewer({ payload, formKey, className }) {
  const { theme } = useTheme();
  const isEmpty =
    !payload ||
    (typeof payload === 'object' && payload !== null && Object.keys(payload).length === 0);

  if (isEmpty) {
    return (
      <Card
        className={cn(
          'border-dashed shadow-none',
          theme.border.light,
          theme.colors.surface,
          className
        )}
      >
        <CardContent className="flex flex-col items-center justify-center py-8 px-4 text-center">
          <DocumentTextIcon className={cn('h-8 w-8 mb-2 opacity-40', theme.text.muted)} />
          <CardDescription className="text-sm m-0">No request details were submitted.</CardDescription>
        </CardContent>
      </Card>
    );
  }

  return <div className={cn('space-y-2.5', className)}>{renderByFormKey(formKey, payload)}</div>;
}

export default TicketingPayloadViewer;
